import { useEffect, useState } from 'react';
import type { ThemeKey } from '@/pages/home/themes';
import {
  CONTENT_INK,
  CONTENT_MUTED,
  CONTENT_BORDER,
  CONTENT_GREEN,
  accentOf,
} from '@/pages/home/contentTokens';
import { setApplyPrefill } from '@/utils/applyPrefill';

interface UserType {
  value: string;
  label: string;
  sub: string;
  badge: string;
  rate: number;
  icon: string;
}

const USER_TYPES: UserType[] = [
  { value: 'jobseeker', label: '구직자', sub: '미취업 · 전액 무료', badge: '자부담 0원', rate: 0, icon: 'ri-user-search-line' },
  { value: 'kua1', label: '국취 1유형', sub: '청년 특별지원', badge: '자부담 5%', rate: 0.05, icon: 'ri-gift-line' },
  { value: 'graduate', label: '졸업예정자', sub: '졸업예정 청년', badge: '자부담 20%', rate: 0.2, icon: 'ri-graduation-cap-line' },
  { value: 'employed', label: '재직자', sub: '고용보험 가입', badge: '자부담 28%', rate: 0.28, icon: 'ri-briefcase-4-line' },
];

const MIN_PRICE = 100;
const MAX_PRICE = 900;
const PRESETS = [237, 371, 480, 650];
const fmt = (n: number) => n.toLocaleString('ko-KR');

export default function TuitionCalculatorSection({
  theme,
  onGoContact,
}: {
  theme: ThemeKey;
  onGoContact: () => void;
}) {
  const accent = accentOf(theme);
  const [type, setType] = useState('jobseeker');
  const [price, setPrice] = useState(480);
  const [gauge, setGauge] = useState(0);
  const [copied, setCopied] = useState(false);
  const [attached, setAttached] = useState(false);

  const opt = USER_TYPES.find((t) => t.value === type) ?? USER_TYPES[0];
  const selfMan = Math.round(price * opt.rate);
  const govMan = price - selfMan;
  const govPct = Math.round((1 - opt.rate) * 100);
  const isFree = opt.rate === 0;
  const tuitionWon = price * 10000;
  const govWon = govMan * 10000;
  const selfWon = selfMan * 10000;
  const pct = ((price - MIN_PRICE) / (MAX_PRICE - MIN_PRICE)) * 100;

  useEffect(() => {
    setGauge(0);
    const t = window.setTimeout(() => setGauge(govPct), 90);
    return () => window.clearTimeout(t);
  }, [govPct]);

  const summary = [
    '[자부담 계산 결과]',
    `고용유형: ${opt.label} (${opt.badge})`,
    `정규 수강료: ${fmt(tuitionWon)}원`,
    `국비 지원금: ${fmt(govWon)}원 (${govPct}%)`,
    `실질 본인 부담금: ${selfMan === 0 ? '0원 (전액 무료)' : `${fmt(selfWon)}원`}`,
  ].join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
    } catch {
      // 클립보드를 지원하지 않는 환경은 무시
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleAttach = () => {
    setApplyPrefill(summary);
    setAttached(true);
    window.setTimeout(() => setAttached(false), 2600);
    onGoContact();
  };

  return (
    <section id="calc" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-24">
      {/* 헤더 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
            자부담 실시간 계산
          </div>
          <h2
            className="font-logo-b text-3xl md:text-4xl leading-tight"
            style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
          >
            내 자부담,<br />0원인지 바로.
          </h2>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <p style={{ fontSize: '15px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.7 }}>
            고용 상태와 정규 수강료를 조절하면, 실제로 내가 결제할 금액과 정부 지원금이
            아래 영수증에 실시간으로 산출됩니다.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* 좌측 — 컨트롤 */}
        <div className="lg:col-span-7">
          <div
            className="rounded-[36px] p-6 md:p-8"
            style={{ border: `1px solid ${CONTENT_BORDER}`, background: `${accent}0a` }}
          >
            {/* 1. 고용 상태 */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: CONTENT_INK }}>
                1 · 내 고용 상태
              </span>
              <span
                className="text-[10px] font-bold px-3 h-[24px] inline-flex items-center rounded-full"
                style={{
                  color: isFree ? CONTENT_GREEN : accent,
                  border: `1px solid ${isFree ? 'rgba(62,142,90,0.35)' : `${accent}55`}`,
                  background: '#fff',
                }}
              >
                {opt.badge}
              </span>
            </div>

            <div role="group" aria-label="내 고용 상태 선택" className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {USER_TYPES.map((t) => {
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setType(t.value)}
                    className="rounded-2xl p-3 text-left cursor-pointer transition-all duration-200"
                    style={{
                      border: `1px solid ${active ? accent : CONTENT_BORDER}`,
                      background: active ? '#fff' : 'transparent',
                      boxShadow: active ? `inset 0 0 0 1px ${accent}` : 'none',
                    }}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <i className={`${t.icon} text-[15px]`} style={{ color: active ? accent : CONTENT_MUTED }} />
                      <span className="text-[10px] font-bold" style={{ color: t.rate === 0 ? CONTENT_GREEN : CONTENT_MUTED }}>
                        {t.rate === 0 ? '0원' : `${Math.round(t.rate * 100)}%`}
                      </span>
                    </div>
                    <div className="text-[12.5px] font-semibold leading-tight" style={{ color: CONTENT_INK }}>
                      {t.label}
                    </div>
                    <div className="text-[10px] mt-0.5" style={{ color: CONTENT_MUTED }}>
                      {t.sub}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 2. 수강료 슬라이더 */}
            <div className="mt-8">
              <div className="flex items-end justify-between mb-4">
                <span className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: CONTENT_INK }}>
                  2 · 정규 수강료
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-logo-b text-[30px] leading-none" style={{ color: accent, letterSpacing: '-0.03em' }}>
                    {price.toLocaleString()}
                  </span>
                  <span className="text-[12px] font-semibold" style={{ color: CONTENT_MUTED }}>만원</span>
                </div>
              </div>

              <div className="relative h-6 flex items-center">
                <div className="absolute left-0 right-0 h-[6px] rounded-full" style={{ background: 'rgba(20,18,16,0.10)' }} />
                <div className="absolute left-0 h-[6px] rounded-full" style={{ width: `${pct}%`, background: accent }} />
                <div
                  className="absolute h-[20px] w-[20px] rounded-full bg-white"
                  style={{ left: `calc(${pct}% - 10px)`, border: `3px solid ${accent}`, boxShadow: '0 2px 8px rgba(20,18,16,0.18)' }}
                />
                <input
                  type="range"
                  min={MIN_PRICE}
                  max={MAX_PRICE}
                  step={10}
                  value={price}
                  onChange={(e) => setPrice(parseInt(e.target.value, 10))}
                  aria-label="정규 수강료 조절"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>

              <div className="flex justify-between text-[10px] mt-2" style={{ color: CONTENT_MUTED }}>
                <span>100만원</span>
                <span>900만원</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-4">
                <span className="text-[11px] mr-1" style={{ color: CONTENT_MUTED }}>빠른 선택</span>
                {PRESETS.map((p) => {
                  const active = price === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPrice(p)}
                      className="px-3 h-[30px] text-[11px] rounded-full font-semibold whitespace-nowrap cursor-pointer transition-all"
                      style={{
                        background: active ? accent : '#fff',
                        color: active ? '#fff' : CONTENT_INK,
                        border: `1px solid ${active ? accent : CONTENT_BORDER}`,
                      }}
                    >
                      {p}만원
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 3. 국비 지원 게이지 */}
            <div
              className="mt-8 rounded-2xl p-4"
              style={{
                border: `1px solid ${isFree ? 'rgba(62,142,90,0.35)' : CONTENT_BORDER}`,
                background: isFree ? 'rgba(62,142,90,0.06)' : '#fff',
              }}
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ color: CONTENT_INK }}>
                  국비 지원 비율
                </span>
                {isFree ? (
                  <span className="text-[11px] font-bold" style={{ color: CONTENT_GREEN }}>
                    실부담 0원 · 전액 무료
                  </span>
                ) : (
                  <span className="text-[11px] font-semibold" style={{ color: CONTENT_MUTED }}>
                    국비 {govPct}% · 자부담 {100 - govPct}%
                  </span>
                )}
              </div>

              <div className="h-[10px] w-full rounded-full overflow-hidden" style={{ background: 'rgba(20,18,16,0.08)' }}>
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{ width: `${gauge}%`, background: isFree ? CONTENT_GREEN : accent }}
                />
              </div>

              <div className="flex justify-between items-center text-[10px] mt-2.5" style={{ color: CONTENT_MUTED }}>
                <span>국비 감면액 {fmt(govWon)}원</span>
                <span>본인 지출 {fmt(selfWon)}원</span>
              </div>
            </div>
          </div>
        </div>

        {/* 우측 — 영수증 */}
        <div className="lg:col-span-5">
          <div
            className="h-full flex flex-col justify-between rounded-[36px] p-6 md:p-7"
            style={{ border: `1px solid ${CONTENT_BORDER}`, background: '#fff' }}
          >
            <div>
              <div className="flex items-center justify-between pb-4" style={{ borderBottom: `1px dashed ${CONTENT_BORDER}` }}>
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
                    <i className="ri-receipt-line text-[15px]" />
                  </span>
                  <div>
                    <div className="text-[12.5px] font-semibold" style={{ color: CONTENT_INK }}>2026 정부지원 모의 산정서</div>
                    <div className="text-[10px]" style={{ color: CONTENT_MUTED }}>실시간 공공데이터 연산</div>
                  </div>
                </div>
                <span className="text-[10px] px-2.5 h-[22px] inline-flex items-center rounded-full" style={{ border: `1px solid ${CONTENT_BORDER}`, color: CONTENT_MUTED }}>
                  #GOV-2026
                </span>
              </div>

              <div className="py-4 space-y-3 text-[12.5px]">
                <div className="flex items-center justify-between" style={{ color: CONTENT_MUTED }}>
                  <span>정규 수강료 정가</span>
                  <span className="font-semibold" style={{ color: CONTENT_INK }}>+{fmt(tuitionWon)}원</span>
                </div>
                <div className="flex items-center justify-between font-semibold" style={{ color: CONTENT_GREEN }}>
                  <span>정부 지원금 ({govPct}%)</span>
                  <span>-{fmt(govWon)}원</span>
                </div>
                <div className="flex items-center justify-between" style={{ color: accent }}>
                  <span className="text-[11px]">K-디지털 훈련장려금 (매월)</span>
                  <span className="text-[11px] font-semibold">최대 +11.6만원/월</span>
                </div>
              </div>

              <div style={{ borderTop: `1px dashed ${CONTENT_BORDER}` }} />

              <div className="py-5 text-center">
                <span className="text-[11px] block mb-1.5" style={{ color: CONTENT_MUTED }}>실질 본인 부담금</span>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span
                    className="font-logo-b text-[40px] leading-none"
                    style={{ color: isFree ? CONTENT_GREEN : CONTENT_INK, letterSpacing: '-0.03em' }}
                  >
                    {selfMan === 0 ? '0원' : `${selfMan}만원`}
                  </span>
                  {isFree && (
                    <span className="text-[10px] font-bold px-2.5 h-[22px] inline-flex items-center rounded-full" style={{ background: 'rgba(62,142,90,0.10)', color: CONTENT_GREEN, border: '1px solid rgba(62,142,90,0.3)' }}>
                      전액 국비
                    </span>
                  )}
                </div>
                <p className="text-[10.5px] mt-2.5" style={{ color: CONTENT_MUTED }}>
                  * 실제 자부담은 운영기관·과정 유형에 따라 달라질 수 있습니다.
                </p>
              </div>
            </div>

            <div className="pt-4 space-y-2.5" style={{ borderTop: `1px solid ${CONTENT_BORDER}` }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="h-[46px] rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2"
                  style={{
                    border: `1px solid ${copied ? CONTENT_GREEN : CONTENT_BORDER}`,
                    color: copied ? CONTENT_GREEN : CONTENT_INK,
                    background: '#fff',
                  }}
                >
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
                  {copied ? '복사 완료' : '📋 결과 복사'}
                </button>
                <button
                  type="button"
                  onClick={handleAttach}
                  className="h-[46px] rounded-full text-[11px] font-semibold text-white whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2"
                  style={{ background: accent }}
                >
                  <i className="ri-attachment-2" />
                  상담 신청서에 결과 첨부
                </button>
              </div>
              {attached && (
                <p className="flex items-center justify-center gap-1.5 text-[10.5px] font-semibold" style={{ color: CONTENT_GREEN }}>
                  <i className="ri-checkbox-circle-fill" />
                  계산 결과가 상담 신청서 문의내용에 첨부되었습니다
                </p>
              )}
              <div className="flex items-center justify-center gap-3 text-[10px]" style={{ color: CONTENT_MUTED }}>
                <span className="flex items-center gap-1"><i className="ri-shield-check-line" style={{ color: CONTENT_GREEN }} />전화번호 불필요</span>
                <span>·</span>
                <span>영업전화 0건</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}